import { LIST_NAME_SUFFIX, } from './cmsBound';

/**
 * Readers for the GraphQL error shape.
 *
 * A GraphQL error holds its HTTP status deep in the response extensions. The
 * path is long, and a caller that walks it by hand repeats the same guards at
 * each step. These functions do the walk one time. Each function gives a safe
 * result if any step of the path is absent.
 */

/** Status for a server-side render that has no better status to give. */
export const SSR_FALLBACK_STATUS = 503;

/**
 * Operations that fill one block of a page.
 *
 * A failure of one of these must not change the page status. The page then
 * renders without that block, and the reader still gets the article.
 *
 * Each other operation fails the whole page. That default is deliberate. A
 * content query that nobody adds to this list keeps the behaviour it had
 * before. The opposite default would let an empty page give status 200, and
 * the CDN would then store that empty page.
 */
export const SECONDARY_OPERATIONS = [
  'GetDefaultMarketingData',
  'GetMarketingData',
  'CommentsCountDataQuery',
  'CommentDataQuery',
  'GetArticleAuthors',
  // `useAuthorsData.js` returns null on an error. Note that `AuthorContentBS`
  // is a different operation, and it is the author page itself.
  'AuthorsQueryBS',
  'TopNewsQuery',
];

const SECONDARY_NAMES = new Set(SECONDARY_OPERATIONS);

/**
 * Does a failure of this operation make the whole page an error?
 *
 * @param {*} operationName the name of the Apollo operation
 * @returns {boolean} true if the render must give an error status
 */
export function failsWholePage(operationName) {
  if (typeof operationName !== 'string') return true;
  if (SECONDARY_NAMES.has(operationName)) return false;
  // A list operation fills a block, and each one shares this name suffix.
  if (operationName.endsWith(LIST_NAME_SUFFIX)) return false;

  return true;
}

/**
 * Reads the HTTP status of the first GraphQL error.
 *
 * The walk runs inside a try block because an error handler must never throw.
 * A property on an error object can be a getter, and that getter can fail.
 *
 * @param {?object[]} graphQLErrors errors from an Apollo response
 * @returns {?number} the status, or null if the errors do not hold one
 */
export function firstErrorStatus(graphQLErrors) {
  try {
    return graphQLErrors?.[0]?.extensions?.response?.status ?? null;
  }
  catch (walkFailure) {
    return null;
  }
}

/**
 * Reads the message of the first error in a list.
 *
 * @param {?object[]} errors any list of GraphQL-shaped errors
 * @returns {?string} the message, or null if the list holds no message
 */
export function firstErrorMessage(errors) {
  try {
    return errors?.[0]?.message ?? null;
  }
  catch (walkFailure) {
    return null;
  }
}

/**
 * Did an upstream service make this status, rather than the page?
 *
 * `graphql-server` turns each failed CMS fetch into status 503, and a fetch
 * that reaches its time limit into 408. Those values describe the upstream. A
 * status such as 404 describes the page, and a reader must always get it.
 *
 * @param {*} status a status that the GraphQL errors carry
 * @returns {boolean} true if an upstream service made the status
 */
export function isUpstreamFailureStatus(status) {
  return status === 408 || (typeof status === 'number' && status >= 500);
}

/**
 * Selects the HTTP status for a server-side render that failed.
 *
 * A verdict about the page always wins, whatever the operation is. A 404 for a
 * page that does not exist must reach the reader.
 *
 * An upstream failure is not such a verdict, so the operation then decides. An
 * operation that fills only a block gives null, and the caller must leave the
 * response status alone. Each other operation gives the upstream status, or the
 * fallback status when the errors carry none.
 *
 * @param {?object[]} graphQLErrors errors from an Apollo response
 * @param {*} [operationName] the name of the Apollo operation
 * @returns {?number} the status to put on the response, or null to change nothing
 */
export function ssrErrorStatus(graphQLErrors, operationName) {
  const carried = firstErrorStatus(graphQLErrors);
  const isVerdictAboutPage = Boolean(carried) && !isUpstreamFailureStatus(carried);

  if (isVerdictAboutPage) return carried;

  if (!failsWholePage(operationName)) return null;

  return carried || SSR_FALLBACK_STATUS;
}
